import { useMemo, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import {
  ArrowLeft,
  ChevronDown,
  ExternalLink,
  Maximize2,
  MessageCircle,
  Minimize2,
  Search,
  Users,
  X,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ChatBubbles } from "@/pages/messages/chat/components/ChatBubbles";
import { ChatInput } from "@/pages/messages/chat/components/ChatInput";
import { TypingIndicator } from "@/pages/messages/chat/components/TypingIndicator";
import {
  useChatController,
} from "@/components/chat-widget/chat-hooks";
import { cn } from "@/lib/utils";
import { roomPresenceSubtitle } from "@/lib/format-presence";

export function StaffChatWidget() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const reduceMotion = useReducedMotion();
  const chat = useChatController();
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [search, setSearch] = useState("");

  const filteredStaff = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return chat.staff.slice(0, 3);
    return chat.staff
      .filter(
        (member) =>
          member.name.toLowerCase().includes(query) ||
          member.email?.toLowerCase().includes(query),
      )
      .slice(0, 3);
  }, [chat.staff, search]);

  const visibleRooms = useMemo(() => {
    const query = search.trim().toLowerCase();
    const rooms = query
      ? chat.rooms.filter((room) => chat.roomTitle(room).toLowerCase().includes(query))
      : chat.rooms;
    return rooms.slice(0, 3);
  }, [chat.roomTitle, chat.rooms, search]);

  const openFullChat = () => {
    const query = chat.activeRoomId
      ? `?room=${encodeURIComponent(chat.activeRoomId)}`
      : "";
    navigate(`/messages/chat${query}`);
    setOpen(false);
    setExpanded(false);
  };

  const closeChat = () => {
    setOpen(false);
    setExpanded(false);
    setMinimized(false);
  };

  const activeRoomSubtitle = chat.activeRoomId
    ? chat.typingUsers.length > 0
      ? "Typing…"
      : roomPresenceSubtitle(chat.activeRoom, {
          currentUserId: user?.id,
          presenceByUserId: chat.presenceByUserId,
        })
    : chat.connected
      ? "Connected"
      : "Reconnecting…";

  const activeRoomOnline =
    chat.activeRoom?.room_type === "staff_direct" && activeRoomSubtitle === "Online";

  return (
    <div className="relative shrink-0">
      <Button
        type="button"
        size="icon"
        data-staff-chat-fab
        className="relative z-[1] h-14 w-14 shrink-0 rounded-2xl border border-primary/20 bg-primary text-primary-foreground shadow-xl ring-4 ring-background/80 transition-transform hover:scale-105"
        aria-label={open ? "Close staff chat" : "Open staff chat"}
        aria-expanded={open}
        onClick={() => {
          setOpen((current) => {
            const next = !current;
            if (!next) {
              setExpanded(false);
              setMinimized(false);
            }
            return next;
          });
        }}
      >
        {open ? <X className="h-5 w-5" /> : <MessageCircle className="h-6 w-6" />}
        {!open && chat.connected && (
          <span className="absolute bottom-1.5 right-1.5 h-2.5 w-2.5 rounded-full border-2 border-primary bg-emerald-400" />
        )}
        <AnimatePresence>
          {chat.totalUnread > 0 && (
            <motion.span
              initial={reduceMotion ? false : { scale: 0 }}
              animate={{ scale: 1 }}
              exit={reduceMotion ? undefined : { scale: 0 }}
              className="absolute -right-1 -top-1 min-w-5 rounded-full bg-destructive px-1 text-[10px] font-bold leading-5 text-destructive-foreground"
            >
              {chat.totalUnread > 99 ? "99+" : chat.totalUnread}
            </motion.span>
          )}
        </AnimatePresence>
      </Button>

      <AnimatePresence>
        {open && (
          <motion.section
            role="dialog"
            aria-label="Staff chat"
            initial={reduceMotion ? false : { opacity: 0, y: 16, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={reduceMotion ? undefined : { opacity: 0, y: 12, scale: 0.98 }}
            transition={{
              opacity: { duration: 0.18 },
              scale: { type: "spring", stiffness: 360, damping: 30 },
            }}
            className={cn(
              "z-[2] flex origin-bottom-right flex-col overflow-hidden rounded-3xl border border-border/80 bg-background/95 shadow-2xl backdrop-blur-xl",
              expanded
                ? "fixed bottom-[10.5rem] right-5 top-4 w-[min(56rem,calc(100vw-2.5rem))]"
                : cn(
                    "absolute bottom-[calc(100%+0.75rem)] right-0",
                    minimized
                      ? "h-14 w-[min(20rem,calc(100vw-2.5rem))]"
                      : "h-[min(30rem,calc(100dvh-10rem))] w-[min(21rem,calc(100vw-2.5rem))]",
                  ),
            )}
          >
            <div className="flex min-h-0 min-w-0 flex-1 flex-col">
              <header className="flex h-16 shrink-0 items-center gap-3 border-b border-border bg-gradient-to-r from-primary/15 to-primary/5 px-4">
                {chat.activeRoomId && !minimized && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 rounded-full"
                    onClick={() => chat.setActiveRoomId(undefined)}
                    aria-label="Back to conversations"
                  >
                    <ArrowLeft className="h-4 w-4" />
                  </Button>
                )}
                <div className="flex h-9 w-9 items-center justify-center rounded-2xl bg-primary text-primary-foreground shadow-md">
                  <MessageCircle className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-bold">
                    {chat.activeRoomId ? chat.roomTitle(chat.activeRoom) : "Staff chat"}
                  </p>
                  <p
                    className={cn(
                      "text-[11px]",
                      activeRoomOnline ? "font-medium text-emerald-600" : "text-muted-foreground",
                    )}
                  >
                    {activeRoomOnline && (
                      <span className="mr-1 inline-block h-1.5 w-1.5 rounded-full bg-emerald-500 align-middle" />
                    )}
                    {activeRoomSubtitle}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setMinimized((current) => !current)}
                  aria-label={minimized ? "Restore chat" : "Minimize chat"}
                >
                  {minimized ? <ChevronDown className="h-4 w-4" /> : <Minimize2 className="h-4 w-4" />}
                </Button>
                {!minimized && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => setExpanded((current) => !current)}
                    aria-label={expanded ? "Compact chat" : "Expand chat"}
                  >
                    <Maximize2 className="h-4 w-4" />
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={closeChat}
                  aria-label="Close chat"
                >
                  <X className="h-4 w-4" />
                </Button>
              </header>

              {!minimized && (
                <>
                  {chat.activeRoomId ? (
                    <motion.div
                      key={chat.activeRoomId}
                      initial={reduceMotion ? false : { opacity: 0, x: 12 }}
                      animate={{ opacity: 1, x: 0 }}
                      className="flex min-h-0 flex-1 flex-col"
                    >
                      <ChatBubbles
                        messages={chat.messages}
                        loading={chat.messagesLoading}
                        users={chat.staff}
                        room={chat.activeRoom}
                      />
                      <TypingIndicator users={chat.typingUsers} />
                      <ChatInput
                        onSend={chat.sendMessage}
                        onTyping={chat.sendTyping}
                        disabled={chat.sending}
                      />
                    </motion.div>
                  ) : (
                    <div className="min-h-0 flex-1 overflow-y-auto p-4">
                      <div className="relative mb-4">
                        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                          value={search}
                          onChange={(event) => setSearch(event.target.value)}
                          placeholder="Find a staff member..."
                          className="h-10 rounded-xl pl-9"
                        />
                      </div>

                      <p className="mb-2 text-xs font-bold uppercase tracking-wide text-muted-foreground">
                        Recent conversations
                      </p>
                      <div className="space-y-1">
                        {visibleRooms.map((room) => (
                          <button
                            key={room.id}
                            type="button"
                            onClick={() => chat.setActiveRoomId(room.id)}
                            className="flex w-full items-center gap-3 rounded-2xl p-3 text-left transition-colors hover:bg-muted"
                          >
                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                              <MessageCircle className="h-4 w-4" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-sm font-semibold">{chat.roomTitle(room)}</p>
                              <p className="text-xs text-muted-foreground">
                                {room.latest_message_at
                                  ? new Date(room.latest_message_at).toLocaleString([], {
                                      month: "short",
                                      day: "numeric",
                                      hour: "numeric",
                                      minute: "2-digit",
                                    })
                                  : "No messages yet"}
                              </p>
                            </div>
                            {room.unread_count > 0 && (
                              <Badge className="min-w-6 justify-center rounded-full">
                                {room.unread_count}
                              </Badge>
                            )}
                          </button>
                        ))}
                        {!chat.roomsLoading && visibleRooms.length === 0 && (
                          <p className="rounded-2xl bg-muted/50 p-4 text-center text-sm text-muted-foreground">
                            No conversations yet. Choose a teammate below.
                          </p>
                        )}
                      </div>

                      <p className="mb-2 mt-5 text-xs font-bold uppercase tracking-wide text-muted-foreground">
                        Start a conversation
                      </p>
                      <div className="space-y-1">
                        {filteredStaff.map((member) => (
                          <button
                            key={member.id}
                            type="button"
                            disabled={chat.creating}
                            onClick={() => void chat.startDirectConversation(member.id)}
                            className="flex w-full items-center gap-3 rounded-2xl p-3 text-left transition-colors hover:bg-muted disabled:opacity-50"
                          >
                            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-muted">
                              {member.name.slice(0, 1).toUpperCase()}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-sm font-semibold">{member.name}</p>
                              <p className="truncate text-xs text-muted-foreground">{member.email}</p>
                            </div>
                            <Users className="h-4 w-4 text-muted-foreground" />
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={openFullChat}
                    className="flex h-11 shrink-0 items-center justify-center gap-2 border-t border-border bg-muted/30 text-xs font-bold text-primary transition-colors hover:bg-muted"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                    Open full chat
                  </button>
                </>
              )}
            </div>
          </motion.section>
        )}
      </AnimatePresence>
    </div>
  );
}
